import { Table, notify } from '@/components';
import { useProjectHomesDeadlinesColumns } from './useProjectHomesDeadlinesColumns';
import {
  ProjectHomeDeadlineInfo,
  UpdateProjectHomeStepDeadlineArgs,
  useDeleteProjectHomePathStepDeadlineMutation,
  useGetProjectsHomesDeadlinesInfoQuery,
  useUpdateProjectHomePathStepDeadlineMutation,
} from '@/redux';
import { useBool } from '@/utils';
import { HomeDeadlineEditModal } from './HomeDeadlineEditModal';
import { useMemo, useState } from 'react';
import {
  ProjectHomeDeadlineFilterParams,
  ProjectHomeStepDeadlineLookupDto,
} from '@/core';
import {
  useDefaultTablePaging,
  useFilters,
} from '@/components/Collections.Paging';
import { useSorting } from '@/components/Collections.Paging/useSorting';

interface Props {
  projectId: string;
  communityId: string;
}

const useSubmit = (closeModal: () => void) => {
  const [add] = useUpdateProjectHomePathStepDeadlineMutation();

  return (args: UpdateProjectHomeStepDeadlineArgs) =>
    add(args)
      .unwrap()
      .then(() => {
        closeModal();
        notify.success.t('projects.homeDeadlines.updated');
      });
};

export const useDeleteOverrideDeadline = () => {
  const [deleteDeadline] = useDeleteProjectHomePathStepDeadlineMutation();

  return (args: UpdateProjectHomeStepDeadlineArgs) => {
    deleteDeadline(args)
      .unwrap()
      .then(() => notify.success.t('projects.homeDeadlines.deleted'));
  };
};

export function ProjectHomesDeadlinesListPanel(props: Props) {
  const { projectId } = props;
  const { setTotal, paging, params } = useDefaultTablePaging();
  const { sorting, onSortingChange } = useSorting<ProjectHomeDeadlineInfo>({
    sortBy: 'homeNumber',
    sortOrder: 'ascend',
  });
  const {
    filters,
    onFiltersChange,
    resetFilters,
    stringFilterOptions,
    setStringFilterOptions,
  } = useFilters<ProjectHomeDeadlineFilterParams>(['number', 'homeNumber']);
  const { data, isFetching } = useGetProjectsHomesDeadlinesInfoQuery({
    projectId,
    paging: params,
    sorting,
    filters,
  });

  const [categoriesCount, setCategoriesCount] = useState(1);

  const deadlines = useMemo(() => {
    if (data) {
      setTotal(data.total);
      setStringFilterOptions(data.stringFilterOptions);
      setCategoriesCount(data.items[0].steps.length);
    }
    return data?.items;
  }, [data, setStringFilterOptions, setTotal]);

  const [openModal, setOpenModal] = useBool(false);
  const [projectHome, setProjectHome] = useState<ProjectHomeDeadlineInfo>();
  const [selectedDeadline, setDeadline] =
    useState<ProjectHomeStepDeadlineLookupDto>();
  const handleDelete = useDeleteOverrideDeadline();
  const columns = useProjectHomesDeadlinesColumns({
    ...props,
    onEditButtonClick: () => setOpenModal(true),
    setProjectHome,
    setDeadline,
    data: deadlines,
    stringFilterOptions: stringFilterOptions,
    onDelete: handleDelete,
  });

  const submit = useSubmit(() => setOpenModal(false));

  return (
    <>
      <Table<ProjectHomeDeadlineInfo>
        name="HomesDeadlines"
        loading={isFetching}
        columns={columns}
        dataSource={deadlines}
        rowKey={(row) => row.home.id}
        pagination={{
          ...paging,
          pageSizeOptions: [10, 20, 50, 100, 200, 500, 1000],
        }}
        onChange={(_, filters, sorting) => {
          onFiltersChange(filters);
          onSortingChange(sorting);
        }}
        onFiltersReset={resetFilters}
        scroll={{ x: categoriesCount * 200 }}
      />
      {openModal && projectHome && selectedDeadline && (
        <HomeDeadlineEditModal
          value={projectHome}
          selectedStep={selectedDeadline}
          onClose={() => setOpenModal(false)}
          onSubmit={submit}
        />
      )}
    </>
  );
}
